import { useEffect, useRef } from "react";
import type { MosaicDirection, MosaicNode } from "react-mosaic-component";
import { useTabs } from "@/context/tabs/TabsContext";
import type { ViewId } from "@/shared/types/common";

export function useReplaceChatWindow(
	removeMosaicWindow: (chatId: ViewId) => MosaicNode<ViewId> | null | undefined,
) {
	const { getTabLayout, setTabLayout } = useTabs();
	const tabLayout = getTabLayout();

	const positionRef = useRef<"first" | "second">("first");

	useEffect(() => {
		const handleDrop = (event: DragEvent) => {
			const viewportWidth = window.innerWidth;
			const dropX = event.clientX;
			const position = dropX < viewportWidth / 2 ? "second" : "first";
			positionRef.current = position;
		};

		document.addEventListener("drop", handleDrop);

		return () => {
			document.removeEventListener("drop", handleDrop);
		};
	}, []);

	useEffect(() => {
		let chatId = "" as ViewId;

		const isChatOpened = (tabLayout: MosaicNode<ViewId> | null): boolean => {
			if (!tabLayout) return false;

			if (typeof tabLayout === "string") {
				if (tabLayout.startsWith("Chat")) {
					chatId = tabLayout as ViewId;
					return true;
				}
				return false;
			}

			const firstPart = tabLayout.first;
			const secondPart = tabLayout.second;

			return isChatOpened(firstPart) || isChatOpened(secondPart);
		};

		const hasChat = isChatOpened(tabLayout);

		if (!hasChat || !tabLayout || typeof tabLayout === "string") return;

		if (
			tabLayout &&
			typeof tabLayout === "object" &&
			"first" in tabLayout &&
			"second" in tabLayout
		) {
			const hasFirstChat =
				typeof tabLayout.first === "string" &&
				tabLayout.first.startsWith("Chat");

			const hasSecondChat =
				typeof tabLayout.second === "string" &&
				tabLayout.second.startsWith("Chat");

			if (hasFirstChat || hasSecondChat) {
				const updatedLayout = {
					...tabLayout,
					direction: "row" as MosaicDirection,
				};
				if (JSON.stringify(updatedLayout) !== JSON.stringify(tabLayout)) {
					setTabLayout(updatedLayout);
				}
				return;
			}
		}

		const stateWithoutChat = removeMosaicWindow(chatId);

		const updatedState = {
			direction: "row" as MosaicDirection,
			[positionRef.current === "first" ? "second" : "first"]:
				chatId as MosaicNode<ViewId>,
			[positionRef.current]: stateWithoutChat,
			splitPercentage: positionRef.current === "first" ? 80 : 20,
		} as unknown as MosaicNode<ViewId>;

		if (JSON.stringify(updatedState) !== JSON.stringify(tabLayout)) {
			setTabLayout(updatedState);
		}
	}, [tabLayout, setTabLayout, removeMosaicWindow]);
}
